import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box } from "@rebass/grid";
import { getFieldInfo, getFieldTitle, } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import { compareAsc } from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocale } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
// eslint-disable-next-line no-restricted-imports
import { enUS as en, fr, nl } from "date-fns/locale";
import React from "react";
import { registerLocale } from "react-datepicker";
import { ts } from "../../../../services";
import { format } from "../../../../utils/date";
import { BaseDatePicker } from "../../../BaseDatePicker";
import { StyledHelpText } from "../../../Form/Form.styled";
import { Labelled } from "../../../Labelled";
import { Select } from "../../../Select";
import { Stack } from "../../../Stack";
import { defineStartYear, defineYearAmount } from "./DateField.util";
registerLocale(ELocale.NL, nl);
registerLocale(ELocale.FR, fr);
registerLocale(ELocale.EN, en);
var MONTH_NAMES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(function (month) {
    return format(new Date("1/1/2000").setMonth(month), "MMMM");
});
export var DateField = function (props) {
    var _a;
    var name = props.name, placeholder = props.placeholder, uiSchema = props.uiSchema, idSchema = props.idSchema, _b = props.formData, formData = _b === void 0 ? null : _b, formContext = props.formContext, _c = props.rawErrors, rawErrors = _c === void 0 ? [] : _c, required = props.required, onChange = props.onChange;
    var initialDate = formData instanceof Date ? formData : new Date(formData);
    var _d = uiSchema.optional, optional = _d === void 0 ? false : _d;
    var _e = React.useState(formData ? initialDate : null), selected = _e[0], setSelected = _e[1];
    var title = getFieldTitle(uiSchema, formContext);
    var info = getFieldInfo(uiSchema, formContext);
    var error = rawErrors[0];
    var $id = idSchema.$id;
    var getDefaultDateFormat = function () {
        var format = "dd/MM/yyyy";
        if (hideDayPicker) {
            format = "MM/yyyy";
        }
        else if (showTimePicker) {
            format = "dd/MM/yyyy HH:mm";
        }
        return format;
    };
    var _f = uiSchema, minDateBase = _f.minDate, maxDateBase = _f.maxDate, showTimePicker = _f.showTimePicker, _g = _f.hideDayPicker, hideDayPicker = _g === void 0 ? false : _g, getMaxDate = _f.getMaxDate, getMinDate = _f.getMinDate, _h = _f.dateFormat, dateFormat = _h === void 0 ? getDefaultDateFormat() : _h, _j = _f.showMonthYearPicker, showMonthYearPicker = _j === void 0 ? false : _j, _k = _f.inverseYears, inverseYears = _k === void 0 ? false : _k;
    var pickerRef = React.useRef(null);
    var id = "".concat(name, "-").concat($id);
    var allFormData = formContext.getAllFormData();
    var isDisabled = ((_a = uiSchema.isDisabled) === null || _a === void 0 ? void 0 : _a.call(uiSchema, allFormData)) || false;
    var minDate = undefined;
    var maxDate = undefined;
    if (minDateBase) {
        minDate = minDateBase;
    }
    if (maxDateBase) {
        maxDate = maxDateBase;
    }
    if (getMinDate) {
        minDate = getMinDate(allFormData);
    }
    if (getMaxDate) {
        maxDate = getMaxDate(allFormData);
    }
    if (typeof minDate === "string") {
        minDate = new Date(minDate);
    }
    if (typeof maxDate === "string") {
        maxDate = new Date(maxDate);
    }
    React.useEffect(function () {
        if (formData === "open-picker") {
            handleIconClick();
            return;
        }
        setSelected(formData ? new Date(formData) : null);
    }, [formData]);
    var getPlaceholder = function () {
        if (placeholder)
            return placeholder;
        else
            return uiSchema.placeholder || ts.datePlaceholder(dateFormat);
    };
    var handleChange = function (input) {
        var compareMax = maxDate ? compareAsc(maxDate, input) : null;
        var compareMin = minDate ? compareAsc(minDate, input) : null;
        var isBeforeMin = compareMin ? compareMin > 0 : null;
        var isAfterMax = compareMax ? compareMax < 0 : null;
        // eslint-disable-next-line no-nested-ternary
        var validatedInput = isBeforeMin ? minDate : isAfterMax ? maxDate : input;
        if (!validatedInput) {
            setSelected(formData);
            return;
        }
        onChange(validatedInput);
        if (formContext.onFieldChange) {
            setTimeout(function () {
                formContext.onFieldChange(name, validatedInput);
            }, 0);
        }
    };
    var yearAmount = defineYearAmount({ minDate: minDate, maxDate: maxDate });
    var yearStart = defineStartYear({ minDate: minDate, maxDate: maxDate, yearAmount: yearAmount });
    var yearsArray = Array(yearAmount)
        .fill(0)
        .map(function (_, idx) { return yearStart + idx; });
    if (inverseYears) {
        yearsArray.reverse();
    }
    var handleClear = function () {
        setSelected(null);
        onChange(null);
        if (formContext.onFieldChange) {
            setTimeout(function () {
                formContext.onFieldChange(name, null);
            }, 0);
        }
    };
    var handleIconClick = function () {
        var _a;
        (_a = pickerRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(true);
    };
    var handleMonthChange = function (month) {
        var newDate = selected || new Date();
        newDate.setMonth(month.value);
        handleChange(newDate);
    };
    var handleYearChange = function (year) {
        var newDate = selected || new Date();
        newDate.setFullYear(year.value);
        handleChange(newDate);
    };
    var fieldMarkup = showMonthYearPicker ? (_jsxs(Stack, { children: [_jsx(Box, { width: 170, children: _jsx(Select, { label: ts.system("month"), labelHidden: true, id: "month-picker", onChange: handleMonthChange, value: selected ? selected.getMonth() : undefined, error: error, options: MONTH_NAMES.map(function (label, value) { return ({
                        value: value,
                        label: label,
                        // isDisabled: selected && maxDate && isAfter(selected.setMonth(value), maxDate),
                    }); }), disabled: isDisabled }) }), _jsx(Box, { width: 130, children: _jsx(Select, { label: ts.system("year"), labelHidden: true, id: "month-picker", onChange: handleYearChange, value: selected ? selected.getFullYear() : undefined, options: yearsArray.map(function (year) { return ({
                        value: year,
                        label: String(year),
                    }); }), disabled: isDisabled }) })] })) : (_jsxs(_Fragment, { children: [_jsx(BaseDatePicker, { id: id, ref: pickerRef, placeholderText: getPlaceholder(), locale: getLocale(), inverseYears: inverseYears, selected: selected, minDate: minDate, maxDate: maxDate, 
                // @ts-ignore
                onChange: handleChange, 
                // onMonthChange={handleChange}
                // onYearChange={handleChange}
                showMonthDropdown: true, showYearDropdown: true, dateFormat: dateFormat, dropdownMode: "select", showTimeSelect: !!showTimePicker, showMonthYearDropdown: true, autoComplete: "off", disabled: isDisabled, 
                // popperPlacement="right-start"
                // popperModifiers={[
                //   // @ts-ignore
                //   {
                //     name: "offset",
                //     options: {
                //       offset: [5, 10],
                //     },
                //   },
                //   // @ts-ignore
                //   {
                //     name: "preventOverflow",
                //     options: {
                //       rootBoundary: "viewport",
                //       tether: false,
                //       altAxis: true,
                //     },
                //   },
                // ]}
                hideDayPicker: hideDayPicker, hasError: error, isDisabled: isDisabled, required: required, handleClear: handleClear, handleIconClick: handleIconClick }), info && _jsx(StyledHelpText, { children: info })] }));
    var handlePressExtraInfo = function () {
        if (formContext.onPressExtraInfo) {
            formContext.onPressExtraInfo(uiSchema.extraInfoUrl);
        }
    };
    return (_jsx(_Fragment, { children: title ? (_jsx(Labelled, { label: title, error: error, optional: optional, onPressExtraInfo: uiSchema.extraInfoUrl && handlePressExtraInfo, children: fieldMarkup })) : (_jsx(_Fragment, { children: "fieldMarkup" })) }));
};
